import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Plus } from 'lucide-react';
import Pagination from '../../components/Pagination';
import Button from '../../components/Button';
import NewsletterPopup from '../../components/NewsletterPopup';
import ConfirmationPopup from '../../components/ConfirmationPopup';
import { postRequest } from "../../utils/networking";
import { apiUrls } from "../../utils/url";
import NoDataFound from '../../components/NoDataFound';
import CommonToastContainer from '../../components/CommonToastContainer';

const DisableButton = ({ rowId, rcid, isEnabled, onToggle, subscriberName }) => {
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = () => {
    setShowConfirmPopup(true);
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      // Call the delete newsletter API with rcid
      const response = await postRequest(apiUrls.deletenewsletter, {
        rcid: rcid,
        enabled: !isEnabled
      });
console.log("Delete API Response:", response);
      // Check if API call was successful
      if (response && response.success !== false) {
        // Close popup first
        setShowConfirmPopup(false);

        // Call parent's onToggle which will refresh the data and show single toast
        await onToggle(rowId, !isEnabled, response);
      } else {
        // Handle API error - show error message from response
        const errorMessage = response?.message || response?.msg || response?.data?.message || "Failed to update subscriber status";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error updating subscriber:", error);
      toast.error(error.message || "Failed to update subscriber status. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowConfirmPopup(false);
  };

  return (
    <>
      <button
        onClick={handleClick}
        disabled={isLoading}
        className={`
          cursor-pointer px-3 py-1 rounded-full text-xs font-medium transition-all duration-200
          ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
          ${isEnabled
            ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
            : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
          }
        `}
      >
        {isLoading ? 'Processing...' : (isEnabled ? 'Disable' : 'Enable')}
      </button>

      <ConfirmationPopup
        isOpen={showConfirmPopup}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title={isEnabled ? "Disable Subscriber" : "Enable Subscriber"}
        message={`Are you sure you want to ${isEnabled ? 'disable' : 'enable'} ${subscriberName}?`}
        confirmText={isEnabled ? "Yes, Disable" : "Yes, Enable"}
        cancelText="Cancel"
        isLoading={isLoading}
      />
    </>
  );
};

const Checkbox = ({ checked, onChange, id, label, disabled = false }) => {
  return (
    <div className="flex items-center gap-2">
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className={`w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 
          ${disabled
            ? 'cursor-not-allowed opacity-50'
            : 'cursor-pointer'
          }`}
      />
      {label && <label htmlFor={id} className={`text-sm font-semibold text-zinc-600 select-none ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>{label}</label>}
    </div>
  );
};

const TableRow = ({ registration, index, onToggle, isSelected, onSelect }) => {
  const isDisabled = !registration.enabled;

  return (
    <div
      className="grid grid-cols-[80px_60px_2fr_1.5fr_120px] min-w-[700px] gap-4 py-3 px-6 hover:bg-zinc-50/50 transition-all duration-200 border-b border-zinc-100"
      data-id={registration.id}
    >
      <div className="flex items-center">
        <Checkbox
          id={`select-${registration.id}`}
          checked={isSelected}
          onChange={() => onSelect(registration.id)}
          disabled={isDisabled}
        />
      </div>

      <div className="flex items-center">
        <span className="text-sm font-semibold text-zinc-800">
          {index + 1}
        </span>
      </div>

      <div className="flex items-center">
        <span className="font-medium text-sm text-zinc-900 truncate" title={registration.serviceName}>
          {registration.serviceName}
        </span>
      </div>

      <div className="flex items-center">
        <span className="text-sm text-zinc-700">
          {registration.subscribedOn || 'N/A'}
        </span>
      </div>

      <div className="flex items-center justify-center">
        <DisableButton
          rowId={registration.id}
          rcid={registration.rcid}
          isEnabled={registration.enabled}
          onToggle={onToggle}
          subscriberName={registration.serviceName}
        />
      </div>
    </div>
  );
};

const NewsLetter = () => {
  const [registrations, setRegistrations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNewsletterData = async (showToast = false, toastMessage = '') => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await postRequest(apiUrls.getnewsletter, {});
      console.log("API Response:", response);

      if (Array.isArray(response)) {
        const transformedData = response.map((item, index) => ({
          id: item.id || index + 1,
          rcid: item.rcid || item.id,
          serviceName: item.email || 'Unknown',
          subscribedOn: item.updatedon || item.date || 'N/A',
          enabled: item.enabled !== undefined ? item.enabled : true,
        }));

        setRegistrations(transformedData);

        // Show single toast message if specified
        if (showToast && toastMessage) {
          toast.success(toastMessage);
        }
      } else {
        setRegistrations([]);
        if (response && response.message && showToast) {
          toast.info(response.message);
        } else if (response && response.message && !showToast) {
          // Don't show toast on initial load
          console.log(response.message);
        }
      }
    } catch (err) {
      console.error('Error fetching newsletter data:', err);
      setError(err.message || 'Failed to load subscribers. Please try again later.');
      if (showToast) {
        toast.error('Failed to load subscribers. Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const updateSubscriberStatus = async (id, enabled, rcid, deleteResponse) => {
    try {
      // Get the message from delete response - look for various common message fields
      const successMessage = deleteResponse?.message || 
                            deleteResponse?.msg || 
                            deleteResponse?.data?.message || 
                            `Subscriber ${enabled ? 'enabled' : 'disabled'} successfully`;

      // Refresh the list and show single toast message
      await fetchNewsletterData(true, successMessage);
      return true;
    } catch (error) {
      console.error("Error updating subscriber status:", error);
      toast.error(error.message || "Failed to update subscriber status");
      return false;
    }
  };

  useEffect(() => {
    fetchNewsletterData(false); // Initial load - no toast
  }, []);

  // Updated search filter to include Joined On date
  const filteredRegistrations = registrations.filter(registration => {
    const searchLower = searchTerm.toLowerCase();
    const nameMatch = registration.serviceName.toLowerCase().includes(searchLower);
    const dateMatch = registration.subscribedOn &&
      registration.subscribedOn.toLowerCase().includes(searchLower);

    return nameMatch || dateMatch;
  });

  const handleToggle = async (id, newEnabledState, deleteResponse) => {
    const registration = registrations.find(reg => reg.id === id);
    if (!registration) return;

    // Call API to update status with rcid (this will refresh the entire list and show single toast)
    const success = await updateSubscriberStatus(id, newEnabledState, registration.rcid, deleteResponse);

    // Clear selected rows after successful operation
    if (success) {
      setSelectedRows([]);
      setCurrentPage(1);
    }
  };

  const handleSelectAll = () => {
    const enabledCurrentPageIds = currentRegistrations
      .filter(reg => reg.enabled)
      .map(reg => reg.id);

    const allEnabledSelected = enabledCurrentPageIds.every(id => selectedRows.includes(id));

    if (allEnabledSelected) {
      setSelectedRows(prev => prev.filter(id => !enabledCurrentPageIds.includes(id)));
    } else {
      const newSelected = [...selectedRows];
      enabledCurrentPageIds.forEach(id => {
        if (!newSelected.includes(id)) {
          newSelected.push(id);
        }
      });
      setSelectedRows(newSelected);
    }
  };

  const handleSelectRow = (id) => {
    const registration = registrations.find(reg => reg.id === id);
    if (!registration || !registration.enabled) return;

    setSelectedRows(prev => {
      if (prev.includes(id)) {
        return prev.filter(rowId => rowId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleGenerateNewsletter = () => {
    const validSelectedRows = selectedRows.filter(id => {
      const registration = registrations.find(reg => reg.id === id);
      return registration && registration.enabled;
    });

    if (validSelectedRows.length === 0) {
      toast.error("Please select at least one subscriber.");
      return;
    }

    setIsPopupOpen(true);
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false);
  };

  const totalItems = filteredRegistrations.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRegistrations = filteredRegistrations.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedRows([]);
    const tableContainer = document.querySelector('.table-scroll-container');
    if (tableContainer) {
      tableContainer.scrollTop = 0;
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    setSelectedRows([]);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
    setSelectedRows([]);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
    setSelectedRows([]);
  };

  const handleRetry = () => {
    fetchNewsletterData(false);
  };

  const enabledCountOnCurrentPage = currentRegistrations.filter(reg => reg.enabled).length;
  const selectedEnabledCount = currentRegistrations.filter(reg => reg.enabled && selectedRows.includes(reg.id)).length;
  const isAllSelected = enabledCountOnCurrentPage > 0 && selectedEnabledCount === enabledCountOnCurrentPage;

  const selectedCount = selectedRows.length;

  return (
    <div className="bg-[#F8F9FE] text-zinc-900 antialiased h-full overflow-hidden">
      <main className="max-w-[1400px] mx-auto h-full flex flex-col p-2 sm:p-3 lg:px-4 lg:py-6 w-full">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
          {/* Left side - Search Box with reduced width */}
          <div className="relative w-full sm:w-64 md:w-80">
            <input
              type="text"
              placeholder="Search by name or date..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full px-6 py-2 pl-10 pr-10 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                aria-label="Clear search"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {selectedCount > 0 && (
              <div className="bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg whitespace-nowrap">
                <span className="text-sm font-medium">
                  {selectedCount} subscriber{selectedCount !== 1 ? 's' : ''} selected
                </span>
              </div>
            )}
            <Button
              className="w-50 px-4 py-2 text-sm font-medium bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:bg-orange-600 hover:-translate-y-0.5 transition-all active:translate-y-0 cursor-pointer"
              onClick={handleGenerateNewsletter}
            >
              <Plus size={18} />
              Generate Newsletter
            </Button>
          </div>
        </div>

        <div className="bg-white/70 rounded-xl shadow-sm border border-zinc-100 flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="overflow-auto flex-1 flex flex-col min-h-0">
            {/* Sticky Header - Always visible */}
            <div className="sticky top-0 z-10 bg-gradient-to-r from-zinc-100 to-zinc-50 border-b border-zinc-200">
              <div className="grid grid-cols-[80px_60px_2fr_1.5fr_120px] min-w-[700px] gap-4 px-6 py-4 text-sm font-semibold text-zinc-600 capitalize">
                <div className="flex items-center">
                  <Checkbox
                    id="select-all"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    label=""
                    disabled={enabledCountOnCurrentPage === 0}
                  />
                </div>
                <div className="flex items-center">S.No</div>
                <div className="flex items-center">Visitor</div>
                <div className="flex items-center">Joined On</div>
                <div className="flex items-center justify-center">Action</div>
              </div>
            </div>

            {/* Scrollable Body with fixed min-height */}
            <div className="table-scroll-container flex-1 relative min-h-[400px]">
              {isLoading ? (
                <div className="absolute inset-0 flex items-center justify-center bg-white/50">
                  <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-amber-600 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
                </div>
              ) : error && registrations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-4">
                  <div className="mb-6">
                    <svg className="w-16 h-16 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Error loading subscribers</h3>
                  <p className="text-sm text-gray-500 mb-4 text-center">{error}</p>
                  <button
                    onClick={handleRetry}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
                  >
                    Try Again
                  </button>
                </div>
              ) : currentRegistrations.length > 0 ? (
                currentRegistrations.map((registration, index) => (
                  <TableRow
                    key={registration.id}
                    registration={registration}
                    index={startIndex + index}
                    onToggle={handleToggle}
                    isSelected={selectedRows.includes(registration.id)}
                    onSelect={handleSelectRow}
                  />
                ))
              ) : (
                <NoDataFound searchTerm={searchTerm} />
              )}
            </div>
          </div>

          {totalItems > 0 && !isLoading && (
            <div className="mt-auto border-t border-zinc-100">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </div>
      </main>

      <NewsletterPopup
        isOpen={isPopupOpen}
        onClose={handlePopupClose}
        selectedSubscribers={selectedRows.map(id =>
          registrations.find(reg => reg.id === id)
        ).filter(sub => sub && sub.enabled)}
      />

      <CommonToastContainer />
    </div>
  );
};

export default NewsLetter;